import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar as CalIcon, Loader2, CreditCard, HandHeart } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import ConfirmationDialog from "@/components/ConfirmationDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMES = ["08:00", "09:30", "11:00", "13:00", "15:00", "16:30", "18:00"];

const SERVICES = [
  { id: "home_session", name: "Home Session — 75 min · $180" },
  { id: "wellness_consult", name: "Wellness Consultation — 45 min · $90" },
  { id: "follow_up", name: "Follow-up Care — 45 min · $110" },
  { id: "healing_plan", name: "Personalised Healing Plan — 4 sessions · $640" },
];

export default function Booking({ presetServiceId, setPresetServiceId }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", address: "", notes: "",
    preferred_time: "", payment_option: "pay_after",
  });
  const [date, setDate] = useState();
  const [service, setService] = useState(presetServiceId || "home_session");
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  // sync incoming preset
  if (presetServiceId && presetServiceId !== service) {
    setService(presetServiceId);
    setPresetServiceId?.(null);
  }

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.address || !date || !form.preferred_time) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        service_id: service,
        preferred_date: format(date, "yyyy-MM-dd"),
      };
      const { data: booking } = await axios.post(`${API}/bookings`, payload);

      if (form.payment_option === "pay_now") {
        const origin = window.location.origin;
        const { data } = await axios.post(`${API}/payments/checkout`, {
          booking_id: booking.id, origin_url: origin,
        });
        window.location.href = data.url;
        return;
      }
      setConfirmation(booking);
      toast.success("Your session is reserved");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="booking" className="py-24 md:py-32 bg-gradient-to-b from-background to-card/40">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="text-center reveal mb-14">
          <p className="text-xs uppercase tracking-[0.25em] text-secondary mb-4">Booking & Payment</p>
          <h2 className="font-serif text-4xl md:text-5xl leading-tight">Start your healing journey</h2>
          <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
            Reserve your session in under a minute. Pay now to confirm, or pay after we meet — both keep your spot.
          </p>
        </div>

        <form
          onSubmit={submit}
          data-testid="booking-form"
          className="reveal rounded-[36px] bg-background border border-border/60 p-7 md:p-12 grid md:grid-cols-2 gap-6"
        >
          <Field label="Full name" id="name"><Input id="name" data-testid="booking-name" value={form.name} onChange={handle("name")} className="rounded-xl bg-card/40 border-border/60 h-12" placeholder="Your full name" /></Field>
          <Field label="Email" id="email"><Input id="email" type="email" data-testid="booking-email" value={form.email} onChange={handle("email")} className="rounded-xl bg-card/40 border-border/60 h-12" placeholder="you@example.com" /></Field>
          <Field label="Phone" id="phone"><Input id="phone" data-testid="booking-phone" value={form.phone} onChange={handle("phone")} className="rounded-xl bg-card/40 border-border/60 h-12" placeholder="0211117540" /></Field>
          <Field label="Address" id="address"><Input id="address" data-testid="booking-address" value={form.address} onChange={handle("address")} className="rounded-xl bg-card/40 border-border/60 h-12" placeholder="Your home address" /></Field>

          <Field label="Service">
            <Select value={service} onValueChange={setService}>
              <SelectTrigger data-testid="booking-service" className="rounded-xl bg-card/40 border-border/60 h-12"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SERVICES.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`booking-service-option-${s.id}`}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Preferred date">
            <Popover>
              <PopoverTrigger asChild>
                <Button type="button" data-testid="booking-date" variant="outline" className="rounded-xl bg-card/40 border-border/60 h-12 justify-start font-normal">
                  <CalIcon size={16} className="mr-2 text-muted-foreground" />
                  {date ? format(date, "PPP") : <span className="text-muted-foreground">Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 rounded-2xl" align="start">
                <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} initialFocus />
              </PopoverContent>
            </Popover>
          </Field>

          <Field label="Preferred time">
            <Select value={form.preferred_time} onValueChange={(v) => setForm((f) => ({ ...f, preferred_time: v }))}>
              <SelectTrigger data-testid="booking-time" className="rounded-xl bg-card/40 border-border/60 h-12"><SelectValue placeholder="Choose a time" /></SelectTrigger>
              <SelectContent>
                {TIMES.map((t) => <SelectItem key={t} value={t} data-testid={`booking-time-option-${t}`}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          <div className="md:col-span-2">
            <Field label="Notes or health concerns (optional)">
              <Textarea data-testid="booking-notes" value={form.notes} onChange={handle("notes")} rows={4} className="rounded-2xl bg-card/40 border-border/60" placeholder="Anything you'd like Farina to know before your session..." />
            </Field>
          </div>

          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3 block">Payment option</Label>
            <RadioGroup value={form.payment_option} onValueChange={(v) => setForm((f) => ({ ...f, payment_option: v }))} className="grid sm:grid-cols-2 gap-4">
              <label htmlFor="pay_now" className={`rounded-2xl border p-5 cursor-pointer transition-all ${form.payment_option === "pay_now" ? "border-secondary bg-secondary/10" : "border-border/60 bg-card/40 hover:border-border"}`}>
                <div className="flex items-start gap-3">
                  <RadioGroupItem value="pay_now" id="pay_now" data-testid="pay-now-radio" className="mt-1" />
                  <div>
                    <div className="flex items-center gap-2 font-serif text-xl">
                      <CreditCard size={16} /> Pay Now
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">Secure payment via Stripe (Card, Apple Pay & Google Pay).</p>
                  </div>
                </div>
              </label>
              <label htmlFor="pay_after" className={`rounded-2xl border p-5 cursor-pointer transition-all ${form.payment_option === "pay_after" ? "border-secondary bg-secondary/10" : "border-border/60 bg-card/40 hover:border-border"}`}>
                <div className="flex items-start gap-3">
                  <RadioGroupItem value="pay_after" id="pay_after" data-testid="pay-after-radio" className="mt-1" />
                  <div>
                    <div className="flex items-center gap-2 font-serif text-xl">
                      <HandHeart size={16} /> Pay After Session
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">Reserve now — settle after we meet, no upfront payment.</p>
                  </div>
                </div>
              </label>
            </RadioGroup>
          </div>

          <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 pt-2">
            <p className="text-xs text-muted-foreground max-w-sm">
              By booking you agree to our gentle cancellation policy: 24-hour notice is appreciated.
            </p>
            <Button
              type="submit"
              data-testid="booking-submit"
              disabled={submitting}
              className="rounded-full px-10 py-6 bg-foreground text-background hover:bg-foreground/85 disabled:opacity-60"
            >
              {submitting ? <><Loader2 className="mr-2 animate-spin" size={16}/> Reserving…</> : (form.payment_option === "pay_now" ? "Continue to payment" : "Reserve my session")}
            </Button>
          </div>
        </form>
      </div>

      <ConfirmationDialog booking={confirmation} onClose={() => setConfirmation(null)} />
    </section>
  );
}

function Field({ label, id, children }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</Label>
      {children}
    </div>
  );
}
